<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md mx-auto bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-6 border-b border-gray-200">
        <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{{ form.title }}</h1>
        <p v-if="form.description" class="text-sm sm:text-base text-gray-600 leading-relaxed">{{ form.description }}</p>
      </div>

      <!-- Form Content -->
      <div class="p-6 sm:p-8">
        <form @submit.prevent="submitForm" class="space-y-5">
          <!-- Honeypot field (hidden from users) -->
          <input
            type="text"
            name="website"
            v-model="honeypot"
            style="position: absolute; left: -9999px;"
            tabindex="-1"
            autocomplete="off"
          />

          <div v-for="(field, index) in form.fields" :key="index" class="space-y-2">
            <label class="block text-sm font-semibold text-gray-700">
              {{ field.label }}
              <span v-if="field.required" class="text-red-500 ml-1">*</span>
            </label>
            <input
              v-if="field.type === 'text' || field.type === 'email' || field.type === 'tel'"
              :type="field.type"
              v-model="formData[getFieldKey(field.label)]"
              :placeholder="field.placeholder || `Enter ${field.label.toLowerCase()}`"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all"
              :class="{
                'focus:ring-green-500': !errorMessage,
                'border-red-300 focus:ring-red-500': errorMessage
              }"
              :style="errorMessage ? {} : { '--tw-ring-color': form.theme_color }"
              :required="field.required"
            />
            <textarea
              v-else-if="field.type === 'textarea'"
              v-model="formData[getFieldKey(field.label)]"
              :placeholder="field.placeholder || `Enter ${field.label.toLowerCase()}`"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all resize-none"
              :class="{
                'focus:ring-green-500': !errorMessage,
                'border-red-300 focus:ring-red-500': errorMessage
              }"
              :style="errorMessage ? {} : { '--tw-ring-color': form.theme_color }"
              rows="4"
              :required="field.required"
            ></textarea>
          </div>

          <button
            type="submit"
            :disabled="submitting"
            class="w-full px-6 py-3.5 rounded-lg font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl active:shadow-md transform hover:-translate-y-0.5 active:translate-y-0 text-base"
            :style="{ backgroundColor: form.theme_color }"
          >
            <span v-if="!submitting" class="flex items-center justify-center gap-2">
              <icon name="send" class="w-5 h-5" />
              Submit
            </span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </span>
          </button>
        </form>

        <!-- Success Message -->
        <div
          v-if="showSuccess"
          class="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg shadow-sm"
        >
          <div class="flex items-center gap-3">
            <icon name="tick" class="w-6 h-6 text-green-600 flex-shrink-0" />
            <p class="text-green-800 font-semibold">{{ form.success_message || 'Thank you for your submission!' }}</p>
          </div>
        </div>

        <!-- Error Message -->
        <div
          v-if="errorMessage"
          class="mt-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-lg shadow-sm"
        >
          <div class="flex items-center gap-3">
            <icon name="cross" class="w-6 h-6 text-red-600 flex-shrink-0" />
            <p class="text-red-800 font-medium">{{ errorMessage }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usePage } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Icon
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Lead Form'
    }
  },
  data() {
    return {
      formData: {},
      honeypot: '',
      submitting: false,
      showSuccess: false,
      errorMessage: null,
    }
  },
  mounted() {
    // Initialize form data
    this.form.fields.forEach(field => {
      const key = this.getFieldKey(field.label)
      this.formData[key] = ''
    })
  },
  methods: {
    getFieldKey(label) {
      // Convert label to slug format for form data
      return label.toLowerCase().replace(/\s+/g, '_')
    },
    async submitForm() {
      // Check honeypot
      if (this.honeypot) {
        // Bot detected
        return
      }

      this.submitting = true
      this.errorMessage = null

      try {
        const response = await this.axios.post(
          this.route('lead-form.submit', this.form.slug),
          this.formData
        )

        if (response.data && response.data.success) {
          this.showSuccess = true
          
          // Show toast notification
          if (usePage().props.flash) {
            usePage().props.flash.success = response.data.message
          }

          // Redirect if URL provided
          if (response.data.redirect_url) {
            setTimeout(() => {
              window.location.href = response.data.redirect_url
            }, 2000)
          } else {
            // Reset form after 3 seconds
            setTimeout(() => {
              this.formData = {}
              this.form.fields.forEach(field => {
                const key = this.getFieldKey(field.label)
                this.formData[key] = ''
              })
              this.showSuccess = false
            }, 3000)
          }
        } else {
          this.errorMessage = response.data.message || 'Failed to submit form. Please try again.'
        }
      } catch (error) {
        console.error('Form submission error:', error)
        let errorMessage = 'Failed to submit form. Please try again.'
        
        if (error.response) {
          if (error.response.status === 422) {
            if (error.response.data?.errors) {
              const errors = Object.values(error.response.data.errors).flat()
              errorMessage = errors[0] || 'Validation failed. Please check your inputs.'
            } else {
              errorMessage = error.response.data?.message || 'Validation failed. Please check your inputs.'
            }
          } else if (error.response.status === 429) {
            errorMessage = 'Too many requests. Please wait a moment and try again.'
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.'
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message
          }
        } else if (error.message) {
          if (error.message.includes('Network') || error.message.includes('timeout')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          } else {
            errorMessage = error.message
          }
        }
        
        this.errorMessage = errorMessage
        if (window.$toast) {
          window.$toast.error(errorMessage)
        }
      } finally {
        this.submitting = false
      }
    }
  },
  watch: {
    'form.title': {
      immediate: true,
      handler(newTitle) {
        document.title = newTitle ? `${newTitle}` : 'Lead Form'
      }
    }
  }
}
</script>

<style scoped>
.lead-form-view {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}
</style>

